import { useCallback, useMemo, useState } from 'react';
import { collection, doc, writeBatch } from 'firebase/firestore';
import { toast } from 'react-toastify';
import { db } from '../../config/firebaseConfig';
import { deleteVisitMirrors, syncVisitMirrors } from '../../utils/hybridVisitMirror';

// Firestore allows 500 writes per batch
const BATCH_LIMIT = 450;

let keySeq = 0;
const newRowKey = () => `new-${++keySeq}`;

// { a: 1, visitEvaluation: { b: 2 } } → { a: 1, 'visitEvaluation.b': 2 }:
// an update then leaves the other visitEvaluation fields alone
const flatten = (fields) => {
  const { visitEvaluation = {}, ...rest } = fields;
  const out = { ...rest };
  Object.entries(visitEvaluation).forEach(([key, value]) => { out[`visitEvaluation.${key}`] = value; });
  return out;
};

// Same project + patient + visit ID means the same visit
const visitKey = (row) => {
  const visit = String(row.visitNumber ?? '').trim().toLowerCase();
  if (!visit) return '';
  return [row.projectId, String(row.patientId ?? '').trim().toLowerCase(), visit].join('|');
};

/**
 * Rows of a manual-entry table and their save. "Tümünü Kaydet" writes only
 * what changed (edited fields of existing visits, new rows), really deletes
 * the removed visits, and keeps whatever failed on screen for a retry.
 *
 * `toFields(row, ctx)` maps a row to Visits fields (no employee / timestamps).
 */
const useEntryRows = ({ labelRef, createBlankRow, toFields, onSaved }) => {
  const [rows, setRows] = useState([]);
  const [removed, setRemoved] = useState([]);
  const [saving, setSaving] = useState(false);

  // Rows straight from Firestore; `_orig` is what the save diffs against
  const setLoaded = useCallback((loaded) => {
    setRows(loaded.map((row) => ({ ...row, _key: row._visitId, _orig: row, _dirty: false })));
    setRemoved([]);
  }, []);

  const change = useCallback((index, field, value) => {
    setRows((prev) => prev.map((row, i) => (i === index ? { ...row, [field]: value, _dirty: true } : row)));
  }, []);

  const addRow = useCallback(() => {
    setRows((prev) => [...prev, { ...createBlankRow(), _key: newRowKey(), _orig: null, _dirty: true }]);
  }, [createBlankRow]);

  const removeRow = (index) => {
    const row = rows[index];
    if (!row) return;
    if (!row._isNew) setRemoved((prev) => [...prev, row]);
    setRows((prev) => prev.filter((r) => r._key !== row._key));
  };

  const pending = useMemo(() => {
    const added = rows.filter((r) => r._isNew).length;
    const changed = rows.filter((r) => !r._isNew && r._dirty).length;
    return { added, changed, removed: removed.length, any: added + changed + removed.length > 0 };
  }, [rows, removed]);

  const save = async (ctx) => {
    const label = labelRef.current;
    if (!pending.any) { toast.info(label('entry.toast.noChanges')); return; }

    // A new row must not repeat a visit that is already in the table
    const seen = new Map();
    for (const row of rows) {
      const key = visitKey(row);
      if (!key) continue;
      if (seen.has(key) && (row._isNew || seen.get(key)._isNew)) {
        toast.warn(label('entry.toast.duplicate', { patient: row.patientId, visit: row.visitNumber }));
        return;
      }
      seen.set(key, row);
    }

    const now = new Date().toISOString();
    const ops = [];
    const unchanged = new Set(); // edited, then typed back to what was saved
    removed.forEach((row) => ops.push({ row, kind: 'delete' }));
    rows.forEach((row) => {
      if (row._isNew) {
        ops.push({
          row,
          kind: 'create',
          data: { ...toFields(row, ctx), employeeId: ctx.employeeId, employeeName: ctx.employeeName, createdAt: now, lastUpdated: now, manualEntry: true },
        });
      } else if (row._dirty) {
        const next = flatten(toFields(row, ctx));
        const before = flatten(toFields(row._orig, ctx));
        const data = Object.fromEntries(Object.entries(next).filter(([key, value]) => value !== before[key]));
        if (Object.keys(data).length > 0) ops.push({ row, kind: 'update', data: { ...data, lastUpdated: now } });
        else unchanged.add(row._key);
      }
    });

    if (ops.length === 0) {
      setRows((prev) => prev.map((row) => (unchanged.has(row._key) ? { ...row, _dirty: false } : row)));
      toast.info(label('entry.toast.noChanges'));
      return;
    }

    setSaving(true);
    const done = {}; // _key → visit id, for every committed op
    let failed = false;
    try {
      for (let i = 0; i < ops.length; i += BATCH_LIMIT) {
        const chunk = ops.slice(i, i + BATCH_LIMIT);
        const batch = writeBatch(db);
        const ids = chunk.map(({ row, kind, data }) => {
          if (kind === 'create') {
            const ref = doc(collection(db, 'Visits'));
            batch.set(ref, data);
            return ref.id;
          }
          const ref = doc(db, 'Visits', row._visitId);
          if (kind === 'delete') batch.delete(ref);
          else batch.update(ref, data);
          return row._visitId;
        });
        await batch.commit();
        chunk.forEach(({ row }, j) => { done[row._key] = ids[j]; });
      }
    } catch (err) {
      console.error('Error saving manual entry:', err);
      failed = true;
    }

    // Committed rows become clean "existing" rows; the rest stay pending
    const isDone = (row) => done[row._key] !== undefined;
    setRows((prev) => prev.map((row) => {
      if (unchanged.has(row._key)) return { ...row, _dirty: false };
      if (!isDone(row)) return row;
      const saved = { ...row, _isNew: false, _visitId: done[row._key], _dirty: false };
      return { ...saved, _orig: saved };
    }));
    setRemoved((prev) => prev.filter((row) => !isDone(row)));

    // Hybrid projects: keep the unblinded worker's copy in step (best effort)
    const mirrorJobs = ops
      .filter(({ row, kind }) => isDone(row) && !row._isMirror && kind !== 'create')
      .map(({ row, kind, data }) => (kind === 'delete'
        ? deleteVisitMirrors(row._visitId)
        : syncVisitMirrors(row._visitId, data)));
    Promise.allSettled(mirrorJobs).then((results) => {
      results.filter((r) => r.status === 'rejected').forEach((r) => console.error('Mirror visit update failed:', r.reason));
    });

    const savedCount = Object.keys(done).length;
    if (savedCount > 0) {
      toast.success(label('entry.toast.saved', { count: savedCount }));
      if (onSaved) onSaved();
    }
    if (failed) toast.error(label('entry.toast.saveFailed', { count: ops.length - savedCount }));
    setSaving(false);
  };

  return { rows, setLoaded, change, addRow, removeRow, pending, save, saving };
};

export default useEntryRows;
